import getCompanyApi from '@/api/get/company/get-company';
import updateCompanyApi from '@/api/patch/company/update-company';
import Alert from '@/components/alert/alert-component';
import SubmitCard from '@/components/card/submit-card-component';
import FormWrapper from '@/components/form-wrapper/form-wrapper-component';
import { useMutation } from '@/hooks/use-api';
import uploadToTempS3Base64 from '@/utils/upload-to-s3-base64';
import { removeUndefinedProperties } from '@/utils/utils';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import theme from '@/common/theme';
import CompanyForm from './company-form';
import Styled from './company-new-style';

/**
 * edit company
 * @returns {JSX.Element}
 */
const CompanyEdit = () => {
  const [initialSuppliers, setInitialSuppliers] = useState([]);
  const navigate = useNavigate();
  const { id } = useParams();

  const [updateCompany, { loading: editCompanyLoading }] =
    useMutation(updateCompanyApi);

  const handleCompanyUpdate = (submitValues) => {
    updateCompany(id, removeUndefinedProperties(submitValues))
      .then(() => {
        navigate('/app/company-management');
        Alert({
          icon: 'success',
          text: 'Company updated successfully',
        });
      })
      .catch(() =>
        Alert({
          icon: 'error',
          text: 'There was a problem updating the company',
        }),
      );
  };

  return (
    <Styled.Main>
      <FormWrapper
        defaultValues={async () => {
          const com = await getCompanyApi(id);
          const originalSuppliers = com.data.suppliers ?? [];
          const supplierIds = originalSuppliers.map((supp) => supp.id);
          setInitialSuppliers(supplierIds);
          delete com.data.id;

          // Initialize selectedSuppliers for the field array
          com.data.selectedSuppliers = originalSuppliers.map((supplier) => ({
            supplierId: supplier.id,
            supplierName: supplier.name || '',
            ueEndpoint: supplier.ue_endpoint || '',
          }));

          // Initialize suppliers field for SelectSearch component
          com.data.suppliers = supplierIds;

          return com.data;
        }}
        onSubmit={(values) => {
          const submitValues = { ...values };
          const { selectedSuppliers } = values;
          if (!submitValues.whiteLabelling.primary_color) {
            submitValues.whiteLabelling.primary_color = theme.COLOR_PRIMARY;
          }
          if (!submitValues.whiteLabelling.secondary_color) {
            submitValues.whiteLabelling.secondary_color = theme.COLOR_SECONDARY;
          }

          // Process suppliers with add, update, and remove operations
          if (selectedSuppliers) {
            const currentSupplierIds = selectedSuppliers.map(
              (s) => s.supplierId,
            );

            // Find suppliers to add (new suppliers not in initial list)
            const add = selectedSuppliers
              .filter(
                (supplier) => !initialSuppliers.includes(supplier.supplierId),
              )
              .map((supplier) => ({
                supplierId: supplier.supplierId,
                ueEndpoint: supplier.ueEndpoint || null,
              }));

            // Find suppliers to remove (initial suppliers not in current list)
            const remove = initialSuppliers.filter(
              (supplierId) => !currentSupplierIds.includes(supplierId),
            );

            // Find suppliers to update (existing suppliers with changed ueEndpoint)
            const update = selectedSuppliers
              .filter((supplier) =>
                initialSuppliers.includes(supplier.supplierId),
              )
              .map((supplier) => ({
                supplierId: supplier.supplierId,
                ueEndpoint: supplier.ueEndpoint || null,
              }));

            submitValues.suppliers = {
              add,
              update,
              remove,
            };
          }

          if (submitValues?.whiteLabelling?.logo?.file) {
            const { file, fileName, fileType } =
              submitValues.whiteLabelling.logo;
            uploadToTempS3Base64(file, fileName, fileType).then((data) => {
              const submitValuesWithLogo = {
                ...submitValues,
                whiteLabelling: {
                  ...submitValues?.whiteLabelling,
                  logo: data.url,
                  logo_id: data.id,
                },
              };
              handleCompanyUpdate(submitValuesWithLogo);
            });
          } else {
            handleCompanyUpdate(submitValues);
          }
        }}
      >
        <CompanyForm title type="edit" isLoading={editCompanyLoading} />
        <SubmitCard
          padding="0"
          primaryButtonTitle="Save"
          isLoading={editCompanyLoading}
          primaryButtonType="submit"
          primaryButtonDisabled={false}
          primaryButtonWidth="112px"
          primaryPermission={['company.update']}
          secondaryButtonType="button"
          secondaryButtonDisabled={false}
          secondaryButtonWidth="112px"
          disabled={editCompanyLoading}
          secondaryButtonTitle="Cancel"
          submitClickSecondary={() => navigate('/app/company-management')}
        />
      </FormWrapper>
    </Styled.Main>
  );
};
export default CompanyEdit;
